import React, { useState, useEffect, useRef } from 'react';
import { GRCEvidence } from '../../types/grc';
import { grcService } from '../../services/grcService';
import { 
  X, Upload, Link as LinkIcon, MessageSquare, FileText, 
  CheckCircle, XCircle, Clock, AlertCircle, Sparkles, 
  Trash2, Eye, Download, Image as ImageIcon
} from 'lucide-react';

interface EvidenceModalProps {
  controlId: number;
  controlCode: string;
  controlTitle: string;
  onClose: () => void;
  onUpdate?: () => void;
}

export const EvidenceModal: React.FC<EvidenceModalProps> = ({
  controlId,
  controlCode,
  controlTitle,
  onClose,
  onUpdate
}) => {
  const [evidenceList, setEvidenceList] = useState<GRCEvidence[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'list' | 'upload'>('list');
  
  // Upload form state
  const [uploadType, setUploadType] = useState<'file' | 'link' | 'comment' | 'image'>('file');
  const [file, setFile] = useState<File | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [commentText, setCommentText] = useState('');
  const [description, setDescription] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadEvidence();
  }, [controlId]);

  const loadEvidence = async () => {
    setLoading(true);
    const data = await grcService.getEvidence(controlId);
    setEvidenceList(data);
    setLoading(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Create preview for images
      if (selectedFile.type.startsWith('image/')) {
        const url = URL.createObjectURL(selectedFile);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file && !linkUrl && !commentText) {
      alert('Please provide evidence (file, link, or comment)');
      return;
    }

    setUploading(true);
    try {
      const result = await grcService.addEvidence(controlId, {
        file: file || undefined,
        description,
        comment_text: commentText || undefined,
        link_url: linkUrl || undefined,
        uploaded_by: 'user'
      });

      if (result.success) {
        // Reset form
        setFile(null);
        setLinkUrl('');
        setCommentText('');
        setDescription('');
        setPreviewUrl(null);
        setActiveTab('list');
        await loadEvidence();
        onUpdate?.();
        
        // Auto-validate with AI if evidence was added
        if (result.evidence_id) {
          await handleValidateEvidence(result.evidence_id);
        }
      } else {
        alert(result.error || 'Failed to upload evidence');
      }
    } catch (error) {
      alert('Error uploading evidence');
    }
    setUploading(false);
  };

  const handleValidateEvidence = async (evidenceId: number) => {
    setValidating(evidenceId);
    try {
      const result = await grcService.validateEvidenceWithAI(controlId, evidenceId);
      if (result.success) {
        await loadEvidence(); // Refresh to show validation results
      }
    } catch (error) {
      console.error('Validation error:', error);
    }
    setValidating(null);
  };

  const handleDeleteEvidence = async (evidenceId: number) => {
    if (!confirm('Are you sure you want to delete this evidence?')) return;
    
    const success = await grcService.deleteEvidence(evidenceId);
    if (success) {
      await loadEvidence();
      onUpdate?.();
    }
  };

  const getStatusBadge = (evidence: GRCEvidence) => {
    const statusConfig = {
      pending: { icon: Clock, color: 'text-yellow-600 bg-yellow-50 border-yellow-200', label: 'Pending Review' },
      accepted: { icon: CheckCircle, color: 'text-green-600 bg-green-50 border-green-200', label: 'Accepted' },
      rejected: { icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200', label: 'Rejected' }
    };
    const config = statusConfig[evidence.status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getAIValidationBadge = (evidence: GRCEvidence) => {
    if (!evidence.ai_validation_status || evidence.ai_validation_status === 'pending') {
      return (
        <button
          onClick={() => handleValidateEvidence(evidence.id)}
          disabled={validating === evidence.id}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border text-purple-600 bg-purple-50 border-purple-200 hover:bg-purple-100"
        >
          <Sparkles className="w-3 h-3" />
          {validating === evidence.id ? 'Validating...' : 'AI Validate'}
        </button>
      );
    }
    
    const aiConfig = {
      validated: { color: 'text-green-600 bg-green-50 border-green-200', label: 'AI Validated' },
      invalid: { color: 'text-red-600 bg-red-50 border-red-200', label: 'AI Invalid' },
      skipped: { color: 'text-gray-600 bg-gray-50 border-gray-200', label: 'AI Skipped' }
    };
    const config = aiConfig[evidence.ai_validation_status] || aiConfig.skipped;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border ${config.color}`}>
        <Sparkles className="w-3 h-3" />
        {config.label}
        {evidence.ai_compliance_score !== null && (
          <span className="ml-1">({evidence.ai_compliance_score}%)</span>
        )}
      </span>
    );
  };

  const getEvidenceIcon = (evidence: GRCEvidence) => {
    if (evidence.file_path?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) return <ImageIcon className="w-5 h-5 text-blue-500" />;
    if (evidence.link_url) return <LinkIcon className="w-5 h-5 text-indigo-500" />;
    if (evidence.comment_text && !evidence.file_path) return <MessageSquare className="w-5 h-5 text-green-500" />;
    return <FileText className="w-5 h-5 text-gray-500" />;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Evidence for {controlCode}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{controlTitle}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('list')}
            className={`flex-1 py-3 text-sm font-medium text-center transition-colors ${
              activeTab === 'list'
                ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Evidence List ({evidenceList.length})
          </button>
          <button
            onClick={() => setActiveTab('upload')}
            className={`flex-1 py-3 text-sm font-medium text-center transition-colors ${
              activeTab === 'upload'
                ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <Upload className="w-4 h-4 inline mr-1" />
            Add Evidence
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'list' && (
            <div className="space-y-3">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="w-8 h-8 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin" />
                </div>
              ) : evidenceList.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No evidence attached yet</p>
                  <button
                    onClick={() => setActiveTab('upload')}
                    className="mt-3 text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Add your first evidence
                  </button>
                </div>
              ) : (
                evidenceList.map((ev) => (
                  <div key={ev.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center border border-gray-200 dark:border-gray-600">
                        {getEvidenceIcon(ev)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white truncate">
                              {ev.original_filename || ev.link_url || 'Comment'}
                            </p>
                            {ev.description && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{ev.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {getStatusBadge(ev)}
                            {getAIValidationBadge(ev)}
                          </div>
                        </div>
                        
                        {ev.comment_text && (
                          <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300">
                            {ev.comment_text}
                          </div>
                        )}
                        
                        {ev.link_url && (
                          <a href={ev.link_url} target="_blank" rel="noopener noreferrer" className="mt-2 text-sm text-indigo-600 hover:underline inline-flex items-center gap-1">
                            <LinkIcon className="w-3 h-3" />
                            {ev.link_url.length > 60 ? ev.link_url.substring(0, 60) + '...' : ev.link_url}
                          </a>
                        )}

                        {ev.ai_validation_result && (
                          <div className={`mt-2 p-2 rounded text-sm ${
                            ev.ai_validation_status === 'validated' 
                              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
                              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
                          }`}>
                            <div className="flex items-center gap-1 font-medium mb-1">
                              <Sparkles className="w-3 h-3" />
                              AI Validation Result
                            </div>
                            {ev.ai_validation_result}
                          </div>
                        )}
                        
                        <div className="mt-2 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                          <span>Uploaded by {ev.uploaded_by}</span>
                          <span>{new Date(ev.uploaded_at).toLocaleDateString()}</span>
                          <div className="flex-1" />
                          {ev.file_path && (
                            <a href={`/uploads/${ev.file_path}`} target="_blank" className="text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
                              <Eye className="w-3 h-3" /> View
                            </a>
                          )}
                          <button onClick={() => handleDeleteEvidence(ev.id)} className="text-red-600 hover:text-red-700 flex items-center gap-1">
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'upload' && (
            <div className="space-y-4">
              {/* Evidence Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Evidence Type</label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { type: 'file', icon: FileText, label: 'Document' },
                    { type: 'image', icon: ImageIcon, label: 'Image' },
                    { type: 'link', icon: LinkIcon, label: 'Link' },
                    { type: 'comment', icon: MessageSquare, label: 'Comment' }
                  ].map(({ type, icon: Icon, label }) => (
                    <button
                      key={type}
                      onClick={() => setUploadType(type as any)}
                      className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-colors ${
                        uploadType === type
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-xs font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* File Upload */}
              {(uploadType === 'file' || uploadType === 'image') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {uploadType === 'image' ? 'Select Image' : 'Select File'}
                  </label>
                  <div 
                    onClick={() => uploadType === 'image' ? imageInputRef.current?.click() : fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400 transition-colors"
                  >
                    {file ? (
                      <div className="space-y-2">
                        {previewUrl && (
                          <img src={previewUrl} alt="Preview" className="max-h-32 mx-auto rounded" />
                        )}
                        <p className="text-sm text-gray-900 dark:text-white">{file.name}</p>
                        <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Click to select {uploadType === 'image' ? 'an image' : 'a file'}
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>
              )}

              {/* Link URL */}
              {uploadType === 'link' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">URL</label>
                  <input
                    type="url"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://example.com/document"
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 p-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}

              {/* Comment Text */}
              {uploadType === 'comment' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Comment</label>
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Enter your evidence comment or notes..."
                    rows={4}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 p-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}

              {/* Description (for all types) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description (optional)</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of this evidence"
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 p-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* AI Validation Notice */}
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-purple-800 dark:text-purple-300">AI Evidence Validation</p>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
                      After upload, AI will automatically analyze if this evidence is compliant with the control requirements.
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleUpload}
                disabled={uploading || (!file && !linkUrl && !commentText)}
                className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload Evidence
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
